import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from '@tanstack/react-router';
import { ChevronDown, LogOut } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useProfileQuery } from '@/graphql/generated';
import { cn } from '@/lib/utils';
import { useAuth } from '@/providers/auth-provider';

interface NavUserTopbarProps {
  className?: string;
}

export const NavUserTopbar: FC<NavUserTopbarProps> = ({
  className,
}: NavUserTopbarProps) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { data } = useProfileQuery();

  const handleLogout = useCallback(async () => {
    logout();
    await navigate({ to: '/auth/login' });
  }, [logout, navigate]);

  const initials = useMemo(() => {
    if (!data?.profile) {
      return '?';
    }

    return `${data.profile.firstName.charAt(0)}${data.profile.lastName.charAt(0)}`.toUpperCase();
  }, [data]);

  if (!user) {
    return null;
  }

  return (
    <div className={cn('flex items-center justify-end ml-auto', className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            data-testid='topbar-user-menu'
            variant='ghost'
            className='hover:bg-muted h-10 cursor-pointer rounded-full py-1 pr-2.5 pl-1'>
            <Avatar className='size-8'>
              <AvatarImage
                src=''
                alt={user.username}
              />
              <AvatarFallback className='bg-brand/15 text-xs font-bold'>
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className='hidden truncate font-semibold sm:inline'>
              {data?.profile.firstName ?? user.username}
            </span>
            <ChevronDown className='size-4 opacity-60' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className='min-w-56 rounded-lg'
          side='bottom'
          align='end'
          sideOffset={4}>
          <DropdownMenuLabel className='p-0 font-normal'>
            <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
              <Avatar className='size-8'>
                <AvatarImage
                  src=''
                  alt={user.username}
                />
                <AvatarFallback className='bg-brand/15 text-xs font-bold'>
                  {initials}
                </AvatarFallback>
              </Avatar>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-semibold'>{user.username}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut />
            {t('auth.logout')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
